import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { messageErreur } from '@/lib/photographe';
import { photographeService, type ChampsFiche, type ChampsPhoto, type SaisieBoutique } from '@/services/photographe.service';
import type { StatutPreparation, TarifPhotographe } from '@/types/photographe';

const CLE = ['photographe'] as const;

// --- Espace du photographe ---

export function useMaFichePhotographe(enabled = true) {
  return useQuery({ queryKey: [...CLE, 'moi'], queryFn: photographeService.moi, enabled, staleTime: 60_000 });
}

export function useTableauDeBordPhotographe(enabled = true) {
  return useQuery({ queryKey: [...CLE, 'tableau-de-bord'], queryFn: photographeService.tableauDeBord, enabled });
}

export function useMesPhotos() {
  return useQuery({ queryKey: [...CLE, 'photos'], queryFn: photographeService.mesPhotos });
}

export function useMesVentes() {
  return useQuery({ queryKey: [...CLE, 'ventes'], queryFn: photographeService.mesVentes });
}

export function useMesReservations() {
  return useQuery({ queryKey: [...CLE, 'reservations'], queryFn: photographeService.mesReservations });
}

/** Tout ce qui touche aux photos change aussi les chiffres du tableau de bord. */
function useRafraichir() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: CLE });
}

export function useModifierFiche() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: (champs: ChampsFiche) => photographeService.modifierMoi(champs),
    onSuccess: () => {
      toast.success('Profil enregistré');
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Enregistrement impossible')),
  });
}

export function useRemplacerTarifs() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: (tarifs: Array<Omit<TarifPhotographe, 'id'> & { id?: string }>) =>
      photographeService.remplacerTarifs(tarifs),
    onSuccess: () => {
      toast.success('Tarifs enregistrés');
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Enregistrement impossible')),
  });
}

export function useModifierBoutique() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: (saisie: SaisieBoutique) => photographeService.modifierBoutique(saisie),
    onSuccess: () => {
      toast.success('Boutique enregistrée');
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Enregistrement impossible')),
  });
}

export function useAvancerCommande() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: (v: { id: string; statut: Exclude<StatutPreparation, 'preparation'> }) =>
      photographeService.avancerCommande(v.id, v.statut),
    onSuccess: (_r, v) => {
      toast.success(v.statut === 'expediee' ? 'Commande marquée expédiée' : v.statut === 'livree' ? 'Commande livrée' : 'Commande retirée');
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Mise à jour impossible')),
  });
}

export function useModifierPhoto() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: ({ id, champs }: { id: string; champs: ChampsPhoto }) => photographeService.modifierPhoto(id, champs),
    onSuccess: () => void rafraichir(),
    onError: (e) => toast.error(messageErreur(e, 'Modification impossible')),
  });
}

export function useSupprimerPhoto() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: (id: string) => photographeService.supprimerPhoto(id),
    onSuccess: () => {
      toast.success('Photo supprimée');
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Suppression impossible')),
  });
}

export function useAgirReservation() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: (v: { id: string; action: 'accepter' | 'refuser' | 'terminer'; photoIds?: string[] }) =>
      photographeService.agirReservation(v.id, v.action, v.photoIds),
    onSuccess: (_r, v) => {
      toast.success(
        v.action === 'accepter' ? 'Réservation acceptée' : v.action === 'refuser' ? 'Réservation déclinée' : 'Photos livrées au club'
      );
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Action impossible')),
  });
}

// --- Public ---

/** Photographes du fil : la liste bouge au rythme des envois, pas de la lecture. */
export function useVitrinePhotographes(limit = 12) {
  return useQuery({
    queryKey: [...CLE, 'vitrine', limit],
    queryFn: () => photographeService.vitrine(limit),
    staleTime: 5 * 60_000,
  });
}

export function usePagePhotographe(slug: string | undefined) {
  return useQuery({
    queryKey: [...CLE, 'page', slug],
    queryFn: () => photographeService.page(slug!),
    enabled: !!slug,
    retry: false,
  });
}

// --- Club ---

export function useAnnuairePhotographes() {
  return useQuery({ queryKey: [...CLE, 'annuaire'], queryFn: photographeService.annuaire });
}

export function useReservationsClub() {
  return useQuery({ queryKey: [...CLE, 'reservations-club'], queryFn: photographeService.reservationsClub });
}

export function useReserverPhotographe() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: photographeService.reserver,
    onSuccess: () => {
      toast.success('Demande envoyée au photographe');
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Réservation impossible')),
  });
}

export function useAnnulerReservationClub() {
  const rafraichir = useRafraichir();
  return useMutation({
    mutationFn: (id: string) => photographeService.annulerReservation(id),
    onSuccess: () => {
      toast.success('Réservation annulée');
      void rafraichir();
    },
    onError: (e) => toast.error(messageErreur(e, 'Annulation impossible')),
  });
}
